import { useNavigate, Link } from "react-router-dom"; // Add Link import
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useGeld } from "../context/GeldContext";
import UserAvatar from "./UserAvatar";
import GeldTracker from "./GeldTracker"; // Import the GeldTracker component
import GeldStore from "./GeldStore"; // Import the GeldStore component
import Inventory from "./Inventory"; // Import the Inventory component
import "./dashboard.css";

// Unit icons and backgrounds - now just 4 units
const unitIcons = {
  1: "➕", // Addition
  2: "➖", // Subtraction
  3: "✖️", // Multiplication
  4: "➗", // Division
};

const unitBackgrounds = {
  1: "#9CDAED", // Light blue for Addition
  2: "#FFD166", // Yellow for Subtraction
  3: "#06D6A0", // Green for Multiplication
  4: "#EF476F", // Pink for Division
};

// Define unit themes and chapters (from ProgressBar.js)
const unitStories = {
  1: {
    theme: "Growing Up",
    icons: {
      20: "baby",
      40: "child",
      60: "teen",
      80: "academy",
    },
    chapters: {
      20: "First Steps",
      40: "Childhood Years",
      60: "Academy Preparation",
      80: "Knight Academy Entrance",
    },
    emojis: {
      20: "👶",
      40: "🧒",
      60: "👦",
      80: "🏫",
    },
  },
  2: {
    theme: "Knight Training",
    icons: {
      20: "shield",
      40: "sword",
      60: "horse",
      80: "graduate",
    },
    chapters: {
      20: "Shield Training",
      40: "Sword Mastery",
      60: "Horseback Riding",
      80: "Knight Graduation",
    },
    emojis: {
      20: "🛡️",
      40: "⚔️",
      60: "🐎",
      80: "🎓",
    },
  },
  3: {
    theme: "The Quest",
    icons: {
      20: "map",
      40: "forest",
      60: "mountain",
      80: "oldman",
    },
    chapters: {
      20: "The Journey Begins",
      40: "Through the Forest",
      60: "Mountain Crossing",
      80: "Meeting the Gold Master",
    },
    emojis: {
      20: "🗺️",
      40: "🌲",
      60: "⛰️",
      80: "🧙‍♂️",
    },
  },
  4: {
    theme: "To The Castle",
    icons: {
      20: "river",
      40: "fire",
      60: "dragon",
      80: "castle",
    },
    chapters: {
      20: "The Great River",
      40: "Trial of Fire",
      60: "The Dragon's Lair",
      80: "Castle Approach",
    },
    emojis: {
      20: "🌊",
      40: "🔥",
      60: "🐉",
      80: "🏰",
    },
  },
};

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const unitsListRef = useRef(null);
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false); // State for inventory modal

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // Auto-scroll to bottom effect remains unchanged
  useEffect(() => {
    // Timeout needed to ensure DOM is fully rendered with all elements
    const timeoutId = setTimeout(() => {
      if (unitsListRef.current) {
        unitsListRef.current.scrollTop = unitsListRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleUnitSelect = (unitNumber) => {
    // Check if the unit is unlocked before navigating
    if (isUnitUnlocked(unitNumber)) {
      navigate(`/learn?unit=${unitNumber}`);
    }
  };

  // Get the actual progress for a unit from user data
  const getUnitProgress = (unitNumber) => {
    // Default to 0 if progress is not set
    return user?.[`unit_${unitNumber}_progress`] || 0;
  };

  // Updated function to determine if a unit is unlocked
  const isUnitUnlocked = (unitNumber) => {
    // Unit 1 is always unlocked
    if (unitNumber === 1) return true;

    // Check for active subscription for units 2-4
    const hasActiveSubscription = user?.active_subscription || false;
    if (!hasActiveSubscription) return false;

    // For units 2-4, check if the previous unit has 80% or more progress
    const previousUnitProgress = getUnitProgress(unitNumber - 1);
    return previousUnitProgress >= 80;
  };

  // Updated function to handle subscription purchase with user UUID
  const handleSubscriptionPurchase = () => {
    // Add the user's UUID as a URL parameter
    if (user && user.id) {
      const stripeBaseUrl = "https://buy.stripe.com/dR65om7sZe612iY8wx";
      const stripeUrlWithUser = `${stripeBaseUrl}?client_reference_id=${user.id}`;
      window.open(stripeUrlWithUser, "_blank");
    } else {
      console.error("Cannot open Stripe: user ID is missing");
    }
  };

  // Check if user should see the subscription prompt
  const shouldShowSubscriptionPrompt = () => {
    // Show if user has 80% or more in unit 1 but no active subscription
    return getUnitProgress(1) >= 80 && !user?.active_subscription;
  };

  // Get current chapter for a unit based on progress
  const getCurrentChapter = (unitNumber) => {
    const progress = getUnitProgress(unitNumber);
    const milestones = [20, 40, 60, 80];

    // If no progress, return the first chapter
    if (progress < 20) {
      return unitStories[unitNumber].chapters[20];
    }

    // Find the highest milestone reached
    let currentChapter = "";
    for (const milestone of milestones) {
      if (progress >= milestone) {
        currentChapter = unitStories[unitNumber].chapters[milestone];
      } else {
        break;
      }
    }

    return currentChapter;
  };

  // Get current chapter emoji for a unit based on progress
  const getCurrentChapterEmoji = (unitNumber) => {
    const progress = getUnitProgress(unitNumber);
    const milestones = [20, 40, 60, 80];

    // If no progress, return the first emoji
    if (progress < 20) {
      return unitStories[unitNumber].emojis[20];
    }

    // Find the highest milestone reached
    let currentEmoji = "";
    for (const milestone of milestones) {
      if (progress >= milestone) {
        currentEmoji = unitStories[unitNumber].emojis[milestone];
      } else {
        break;
      }
    }

    return currentEmoji || unitStories[unitNumber].emojis[20];
  };

  // Updated units data - just 4 units now
  const units = [
    { id: 1, name: "Addition", symbol: "+" },
    { id: 2, name: "Subtraction", symbol: "−" },
    { id: 3, name: "Multiplication", symbol: "×" },
    { id: 4, name: "Division", symbol: "÷" },
  ];

  const currentUnit = user?.cur_unit || 1;

  // The milestone positions for progress bar
  const milestones = [20, 40, 60, 80];

  return (
    <div className="full-page-container">
      <style jsx="true">{`
        /* Add new styles for enhanced dashboard */
        .dashboard-logo-container {
          display: flex;
          align-items: center;
        }

        .links-logo {
          height: 50px; /* Increased height */
          width: auto; /* Auto width to maintain aspect ratio */
          border-radius: 0; /* Remove circular styling */
          object-fit: contain; /* Show whole image */
          margin-right: 10px;
        }

        /* Improved header layout */
        .dashboard-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          padding: 10px 15px;
          width: 100%;
        }

        .header-center {
          justify-self: center; /* redundant but safe */
        }

        .header-left {
          justify-self: start;
        }

        .header-right {
          justify-self: end;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .user-section {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .logout-button {
          white-space: nowrap;
          padding: 6px 12px;
        }

        /* Responsive styles for mobile - improved */
        @media (max-width: 600px) {
          .dashboard-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 8px 4px;
            gap: 0;
          }
          .header-center {
            order: 1;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .header-left {
            order: 2;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 8px;
          }
          .header-right {
            order: 3;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 8px;
          }

          .links-logo {
            height: 40px;
          }

          .user-info {
            gap: 5px;
          }

          .username {
            font-size: 14px;
            max-width: 80px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .logout-button {
            font-size: 12px;
            padding: 4px 8px;
          }

          /* Improved unit box styling for mobile */
          .unit-box {
            padding: 8px 5px;
            min-height: 150px; /* Set minimum height */
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }

          .unit-box-title-container {
            margin-bottom: 0;
          }

          .unit-title {
            font-size: 10px;
            margin: 0 0 2px 0;
          }

          .unit-theme-chapter {
            font-size: 11px;
            margin-top: 2px;
            line-height: 1.2;
          }

          .chapter-emoji {
            font-size: 16px;
            margin: 2px auto;
          }

          .unit-box-content {
            flex: 0 0 auto;
            margin: 5px 0;
          }

          .unit-symbol {
            font-size: 16px;
          }

          .unit-box-progress-wrapper {
            margin-top: 5px;
            padding: 0 5px;
          }

          .unit-box-progress-indicator {
            font-size: 9px;
            top: -14px;
          }

          .unit-box-progress-container {
            height: 8px;
          }

          .milestone-marker {
            width: 10px;
            height: 10px;
          }

          .unit-box-percent {
            font-size: 10px;
            margin-top: 10px;
          }

          .practice-button {
            font-size: 14px;
            padding: 8px 15px;
          }
        }

        /* Regular styles */
        .unit-theme-chapter {
          margin-top: 5px;
          font-size: 14px;
          color: rgba(0, 0, 0, 0.7);
          font-style: italic;
          text-align: center;
        }

        .chapter-emoji {
          font-size: 40px;
          display: block;
          margin: 5px auto;
          text-align: center;
        }

        .milestone-marker {
          position: absolute;
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background-color: #f3e1b2;
          border: 2px solid #fff;
          transform: translate(-50%, -50%);
          z-index: 2;
          top: 50%;
        }

        .milestone-marker.active {
          background-color: #ff9e3d;
        }

        .user-avatar-marker {
          position: absolute;
          transform: translate(-50%, -50%);
          z-index: 3;
          top: 50%;
        }

        .unit-box-progress-container {
          position: relative;
          height: 10px;
        }

        /* Completely revised mobile styles */
        @media (max-width: 767px) {
          /* Header styles */
          .dashboard-header {
            padding: 8px;
          }

          .links-logo {
            height: 40px;
          }

          .user-info {
            gap: 5px;
          }

          .username {
            font-size: 14px;
            max-width: 80px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .logout-button {
            font-size: 12px;
            padding: 4px 8px;
          }

          /* Complete removal of theme/chapter elements on mobile */
          .unit-theme-chapter,
          .chapter-emoji {
            display: none;
          }

          /* Simplified unit boxes for mobile */
          .unit-box {
            padding: 8px 5px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 120px;
            gap: 8px;
          }

          .unit-box-title-container {
            margin-bottom: 0;
          }

          .unit-title {
            font-size: 14px;
            margin: 0 0 5px 0;
            text-align: center;
            font-weight: bold;
          }

          /* Fixed unit symbol sizing */
          .unit-box .unit-symbol {
            font-size: 60px !important; /* Add !important to override any inline styles */
            margin: 0;
            text-align: center;
            display: block;
            line-height: 1;
          }

          .unit-box-content {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-grow: 1;
            height: 30px; /* Constrain height to make layout more consistent */
          }

          /* Progress bar adjustments */
          .unit-box-progress-wrapper {
            margin-top: 5px;
            padding: 0 5px;
          }

          .unit-box-progress-container {
            height: 8px;
          }

          .milestone-marker {
            width: 8px;
            height: 8px;
          }

          .unit-box-percent {
            font-size: 12px;
            margin-top: 5px;
            text-align: center;
          }

          .user-avatar-marker {
            transform: translate(-50%, -50%) scale(0.8);
          }
        }

        /* New styles for subscription banner */
        .subscription-banner {
          background: linear-gradient(135deg, #6e8efb, #a777e3);
          color: white;
          padding: 15px;
          border-radius: 8px;
          margin: 10px 15px;
          text-align: center;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .subscription-banner h3 {
          margin: 0;
          font-size: 18px;
        }

        .subscription-banner p {
          margin: 5px 0;
          font-size: 14px;
        }

        .price-tag {
          font-weight: bold;
          font-size: 16px;
          margin: 5px 0;
        }

        .subscribe-button {
          background-color: #4caf50;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          cursor: pointer;
          font-weight: bold;
          transition: background-color 0.3s;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .subscribe-button:hover {
          background-color: #388e3c;
        }

        /* Show padlock icon on locked units */
        .unit-box.subscription-locked {
          position: relative;
          opacity: 0.7;
          cursor: not-allowed;
        }

        .subscription-lock-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
        }

        .subscription-lock-icon {
          font-size: 48px;
        }

        /* Unit symbol positioning */
        .unit-box {
          position: relative; /* Add this to create positioning context */
        }

        .unit-box-content {
          position: static; /* Change from flex to static */
        }

        .unit-symbol {
          position: absolute; /* Position absolutely */
          top: 50%; /* Center vertically */
          left: 50%; /* Center horizontally */
          transform: translate(-50%, -50%); /* True centering */
          font-size: 100px; /* Consistent size */
          z-index: 2; /* Ensure symbol is on top */
          line-height: 1; /* Proper line height */
        }

        /* Responsive styles for mobile - improved */
        @media (max-width: 600px) {
          // ...existing code...

          .store-button,
          .inventory-button {
            font-size: 12px;
            padding: 4px 8px;
          }
        }

        /* Regular styles */
        // ...existing code...

        /* Completely revised mobile styles */
        @media (max-width: 767px) {
          // ...existing code...

          /* Fixed unit symbol sizing and positioning */
          .unit-box .unit-symbol {
            font-size: 40px !important; /* Override any inline styles */
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            margin: 0;
            line-height: 1;
          }

          .unit-box-content {
            position: static;
            height: 60px; /* Maintain space for the absolutely positioned symbol */
            width: 100%;
          }
        }

        /* Add new styles for the centered logo header */
        .dashboard-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          padding: 10px 15px;
          width: 100%;
        }

        .header-left {
          justify-self: start;
        }

        .header-center {
          justify-self: center;
        }

        .header-right {
          justify-self: end;
        }

        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .logo-text {
          font-weight: bold;
          font-size: 20px; /* Increased from 18px */
          margin-bottom: 4px; /* Increased from 2px for more spacing */
          margin-right: 10px;
          color: #333;
          font-family: inherit; /* Match font with rest of dashboard */
          align-items: center;
        }

        .links-logo {
          height: 50px; /* Increased from 40px */
          width: auto;
          object-fit: contain;
        }

        @media (max-width: 600px) {
          .logo-text {
            font-size: 18px; /* Increased from 16px */
          }

          .links-logo {
            height: 40px; /* Increased from 35px */
          }
        }

        /* Rest of your existing styles */
        .dashboard-logo-container {
          display: flex;
          align-items: center;
        }

        /* ...existing code... */

        /* Add styles for footer links */
        .footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          padding: 10px 0;
          margin-top: 10px;
          width: 100%;
        }

        .footer-link {
          color: #555;
          text-decoration: none;
          font-size: 12px;
        }

        .footer-link:hover {
          text-decoration: underline;
        }

        @media (max-width: 600px) {
          .footer-links {
            padding: 5px 0;
            margin-top: 5px;
          }
        }

        /* Store button styles */
        .store-button {
          background-color: #4caf50;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 6px 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          gap: 5px;
          margin-left: 5px;
        }

        .store-button:hover {
          background-color: #388e3c;
        }

        /* Inventory button styles */
        .inventory-button {
          background-color: #ff9800; /* Orange color */
          color: white;
          border: none;
          border-radius: 12px;
          padding: 6px 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          gap: 5px;
          margin-left: 5px; /* Space between store and inventory buttons */
        }

        .inventory-button:hover {
          background-color: #f57c00; /* Darker orange */
        }

        .geld-section {
          display: flex;
          align-items: center;
        }

        /* Responsive styles for mobile - improved */
        @media (max-width: 600px) {
          // ...existing code...

          .store-button,
          .inventory-button {
            font-size: 12px;
            padding: 4px 8px;
          }
        }

        /* Regular styles */
        // ...existing code...
      `}</style>

      <div className="dashboard-wrapper">
        {/* Updated header with centered Links logo and text */}
        <header className="dashboard-header">
          <div className="header-left">
            <div className="user-info">
              {/* Add UserAvatar */}
              <UserAvatar user={user} size={30} />
              <div className="username">{user?.username || "Learner"}</div>
              {/* Add Geld section with tracker and store/inventory buttons */}
              <div className="geld-section">
                {user && <GeldTracker />}
                <button
                  className="store-button"
                  onClick={() => setIsStoreOpen(true)}
                >
                  Store
                </button>
                <button
                  className="inventory-button"
                  onClick={() => setIsInventoryOpen(true)}
                >
                  {" "}
                  {/* Add Inventory button */}
                  Items
                </button>
              </div>
            </div>
          </div>

          <div className="header-center">
            <div className="logo-container">
              <div className="logo-text">Links</div>
              <img
                src="/links_avatar/normal_links.png"
                alt="Links"
                className="links-logo"
              />
            </div>
          </div>

          <div className="header-right">
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        {/* Subscription banner - show when appropriate */}
        {shouldShowSubscriptionPrompt() && (
          <div className="subscription-banner">
            <h3>You've completed 80% of Unit 1!</h3>
            <p>Unlock all units and continue your journey with Links.</p>
            <div className="price-tag">One-time payment of $3.99</div>
            <button
              className="subscribe-button"
              onClick={handleSubscriptionPurchase}
            >
              <span>🔓</span> Unlock All Units
            </button>
          </div>
        )}

        {/* Unit grid takes available space */}
        <div className="unit-grid">
          {units.map((unit) => {
            const progress = getUnitProgress(unit.id);

            // Determine if unit is locked due to progression or subscription
            const isProgressionLocked =
              unit.id > 1 && getUnitProgress(unit.id - 1) < 80;
            const isSubscriptionLocked =
              unit.id > 1 && !user?.active_subscription;
            const isLocked = isProgressionLocked || isSubscriptionLocked;

            // Current unit is the first unlocked unit with < 100% progress
            const isCurrentUnit =
              !isLocked &&
              progress < 100 &&
              (unit.id === 1 || getUnitProgress(unit.id - 1) >= 80);

            // Get unit theme and current chapter for unlocked units
            const unitTheme = unitStories[unit.id].theme;
            const currentChapter = isLocked ? "" : getCurrentChapter(unit.id);
            const chapterEmoji = isLocked
              ? ""
              : getCurrentChapterEmoji(unit.id);

            return (
              <div
                key={unit.id}
                className={`unit-box ${isLocked ? "locked" : ""} ${
                  isCurrentUnit ? "current" : ""
                } ${
                  isSubscriptionLocked && !isProgressionLocked
                    ? "subscription-locked"
                    : ""
                }`}
                onClick={
                  !isLocked ? () => handleUnitSelect(unit.id) : undefined
                }
                style={{
                  backgroundColor: unitBackgrounds[unit.id],
                  flexShrink: 0,
                }}
              >
                {/* Unit title moved to top */}
                <div className="unit-box-title-container">
                  <h2 className="unit-title">{unit.name}</h2>
                  {/* Add unit theme and current chapter for unlocked units */}
                  {!isLocked && (
                    <>
                      <div className="unit-theme-chapter">
                        {unitTheme}: {currentChapter}
                      </div>
                      <span className="chapter-emoji">{chapterEmoji}</span>
                    </>
                  )}
                </div>

                {/* Unit content with absolutely positioned symbol */}
                <div className="unit-box-content">
                  <div className="unit-symbol">{unit.symbol}</div>
                </div>

                {/* Enhanced progress bar with milestones and avatar */}
                <div className="unit-box-progress-wrapper">
                  {/* Current progress indicator moved above the progress point */}
                  {!isLocked && (
                    <div
                      className="unit-box-progress-indicator"
                      style={{ left: `${progress}%` }}
                    >
                      {progress}%
                    </div>
                  )}

                  <div className="unit-box-progress-container">
                    {/* Milestone markers */}
                    {!isLocked &&
                      milestones.map((milestone) => (
                        <div
                          key={milestone}
                          className={`milestone-marker ${
                            progress >= milestone ? "active" : ""
                          }`}
                          style={{ left: `${milestone}%` }}
                          title={unitStories[unit.id].chapters[milestone]}
                        />
                      ))}

                    {/* Progress bar */}
                    <div
                      className="unit-box-progress-bar"
                      style={{ width: `${progress}%` }}
                    />

                    {/* User avatar at current progress */}
                    {!isLocked && (
                      <div
                        className="user-avatar-marker"
                        style={{ left: `${progress}%` }}
                      >
                        <UserAvatar user={user} size={20} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Show lock overlay if locked due to progression */}
                {isProgressionLocked && (
                  <div className="unit-lock-overlay">
                    <span className="lock-icon">🔒</span>
                  </div>
                )}

                {/* Show subscription lock overlay - now using regular lock icon */}
                {isSubscriptionLocked && !isProgressionLocked && (
                  <div className="subscription-lock-overlay">
                    <span className="subscription-lock-icon">🔒</span>
                  </div>
                )}

                {/* Percentage for non-locked units */}
                {!isLocked && (
                  <div className="unit-box-percent">{progress}%</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Practice button - more compact */}
        <div className="practice-section">
          <button
            className="practice-button"
            onClick={() => navigate("/practice")}
          >
            <span className="practice-icon">🏆</span>
            <span>Practice Mode</span>
          </button>
        </div>

        {/* Add footer links */}
        <div className="footer-links">
          <Link to="/terms" className="footer-link">
            Terms of Service
          </Link>
          <Link to="/privacy" className="footer-link">
            Privacy Policy
          </Link>
          <a
            href="mailto:brockman.michael2003@gmail.com?subject=Links%20Support%20Request"
            className="footer-link"
          >
            Contact Us
          </a>
        </div>
      </div>
      {/* Render modals */}
      {isStoreOpen && <GeldStore onClose={() => setIsStoreOpen(false)} />}
      {isInventoryOpen && (
        <Inventory onClose={() => setIsInventoryOpen(false)} />
      )}
    </div>
  );
};

export default Dashboard;
